import { useEffect, useMemo, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Maximize2, Minimize2, Settings } from "lucide-react";
import Logo from "@/assets/svg/logoBlue.svg?react";
import { useTheme } from "@/contexts/Theme";
import { bottomNavItems, navItems, topNavItems } from "@/constants/sidebar";
import { NavLink } from "react-router-dom";
import useTranslate from "@/hooks/useTranslate";
import WorkspaceSwitcher from "../auth/WorkspaceSwitcher";
import { useWorkspaces } from "@/api/auth/queries";
import { INavItem } from "@/types/routing";

type Props = {
  navList: Array<INavItem>;
  isExpanded?: boolean;
};

const MenuList = ({ navList, isExpanded }: Props) => {
  const t = useTranslate();
  const { theme } = useTheme();
  const isDark = useMemo(() => theme === "dark", [theme]);

  return (
    <div
      className={`flex lg:flex-col gap-2 px-4 lg:gap-4 w-full lg:mt-2 ${isExpanded ? "" : "items-center"}`}>
      {navList.map((item) => (
        <NavLink
          to={item.path}
          key={item.path}
          className={`${item.hideOnMobile ? "" : "w-1/4 lg:w-auto"} ${item.soon ? "cursor-wait pointer-events-none" : ""}`}>
          {({ isActive }: { isActive: boolean }) => (
            <Button
              variant="ghost"
              className={`
              rounded-full lg:rounded-xl
            ${item.hideOnMobile ? "hidden lg:flex" : ""} flex-col lg:flex-row w-full h-14 relative ${item.soon ? "pointer-events-none cursor-default" : ""} ${isActive ? (isDark ? "bg-slate-700" : "bg-slate-200") : ""}  ${isDark ? "text-white" : "text-slate-700"} ${isExpanded ? "justify-start" : ""}`}>
              <div className={isExpanded ? "w-1/5" : ""}>{item.icon} </div>
              <div
                className={`hidden md:block lg:pl-2 w-4/5 text-center lg:text-start ${!isExpanded ? "lg:hidden" : ""}`}>
                {t(item.namePath)}
              </div>
              {item.soon ? (
                <div
                  className={`absolute text-white px-2 py-1 rounded-full  bg-boudrawL -top-2 -right-2 w-4 h-4 text-[0px] ${isExpanded ? "lg:w-auto lg:h-auto lg:top-0 lg:-right-1 lg:text-xs" : ""}`}>
                  soon
                </div>
              ) : null}
            </Button>
          )}
        </NavLink>
      ))}
    </div>
  );
};

const Menu = ({}) => {
  const [isInternalExpanded, setIsExpanded] = useState<boolean | undefined>(
    undefined
  );
  const { theme } = useTheme();
  const isDark = useMemo(() => theme === "dark", [theme]);
  const { refetch } = useWorkspaces();

  useEffect(() => {
    const expanded = localStorage.getItem("sidebar-expanded");
    if (expanded !== null && expanded !== undefined) {
      setIsExpanded(JSON.parse(expanded));
    }
  }, []);

  useEffect(() => {
    if (isInternalExpanded === undefined) return;
    localStorage.setItem(
      "sidebar-expanded",
      JSON.stringify(isInternalExpanded)
    );
  }, [isInternalExpanded]);

  useEffect(() => {
    refetch();
  }, []);

  const isExpanded = isInternalExpanded ?? false;

  return (
    <div
      className={`
      w-full box-border ${isExpanded ? "lg:w-64" : "lg:w-24"} shadow-sm lg:h-full`}>
      <Card
        className={`
      rounded-none border-r border-l-0 border-t-0 border-b-0
      lg:mx-0 lg:my-0
      flex h-full p-6 lg:px-0 py-4 relative flex-col items-center transition-all`}>
        <div
          className={`hidden lg:flex w-full items-center ${isExpanded ? "px-4 justify-between flex-row" : "justify-center flex-col"}`}>
          <div
            className={`${isDark ? "" : "text-slate-900"} w-full px-2 py-4 flex ${isExpanded ? "justify-start" : "justify-center"}`}>
            <Logo className="w-12 h-12" />
          </div>
          <Button
            variant="ghost"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="rounded-xl w-12 h-12 p-4">
            {isExpanded ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>
        </div>
        <Separator className="hidden lg:block my-3" />
        <div className="hidden lg:flex flex-col w-full h-full justify-between">
          <MenuList navList={topNavItems} isExpanded={isExpanded} />
          <div>
            <div className="w-full px-4 my-2">
              <WorkspaceSwitcher isExpanded={isExpanded} />
            </div>
            <Separator className="my-4" />
            <MenuList navList={bottomNavItems} isExpanded={isExpanded} />
          </div>
        </div>
        <div className="w-full flex lg:hidden">
          <MenuList navList={navItems} />
        </div>
      </Card>
    </div>
  );
};

export default Menu;
